import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="spinner"
export default class extends Controller {
  static targets = [ "container"]

  connect() {
    // Core lifecycle bindings
    this.onBeforeVisit = this.show.bind(this)
    this.onTurboLoad = this.hide.bind(this)
    this.onSpinnerShow = this.show.bind(this)
    this.onSpinnerHide = this.hide.bind(this)
    this.onBeforeStreamRender = this.hide.bind(this)

    // Additional events for forms, Turbo and Rails UJS
    this.onTurboSubmitStart = this.show.bind(this)
    this.onTurboSubmitEnd = this.hide.bind(this)
    this.onTurboBeforeFetchRequest = this.show.bind(this)
    this.onTurboBeforeFetchResponse = this.hide.bind(this)
    this.onAjaxSend = this.show.bind(this)
    this.onAjaxComplete = this.hide.bind(this)
    this.onTurboFrameLoad = this.hide.bind(this)

    // Register listeners
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("turbo:load", this.onTurboLoad)
    document.addEventListener("spinner:show", this.onSpinnerShow)
    document.addEventListener("spinner:hide", this.onSpinnerHide)
    document.addEventListener("turbo:before-stream-render", this.onBeforeStreamRender)

    document.addEventListener("turbo:submit-start", this.onTurboSubmitStart)
    document.addEventListener("turbo:submit-end", this.onTurboSubmitEnd)
    document.addEventListener("turbo:before-fetch-request", this.onTurboBeforeFetchRequest)
    document.addEventListener("turbo:before-fetch-response", this.onTurboBeforeFetchResponse)

    // Extra Turbo render events (cover cases where frames/html are replaced)
    document.addEventListener("turbo:before-render", this.onBeforeStreamRender)
    document.addEventListener("turbo:frame-render", this.onTurboFrameLoad)

    // AJAX (Rails UJS / jquery-ujs) events - cover multiple event names used across versions
    document.addEventListener("ajax:send", this.onAjaxSend)
    document.addEventListener("ajax:beforeSend", this.onAjaxSend)
    document.addEventListener("ajax:success", this.onAjaxComplete)
    document.addEventListener("ajax:error", this.onAjaxComplete)
    document.addEventListener("ajax:complete", this.onAjaxComplete)

    // jQuery global AJAX events (if jQuery is present)
    if (window.jQuery) {
      this._jqAjaxSendHandler = () => this.show()
      this._jqAjaxCompleteHandler = () => this.hide()
      jQuery(document).on('ajaxSend.spinner', this._jqAjaxSendHandler)
      jQuery(document).on('ajaxComplete.spinner ajaxError.spinner ajaxSuccess.spinner', this._jqAjaxCompleteHandler)
    }

    // Frame load hides spinner as well
    document.addEventListener("turbo:frame-load", this.onTurboFrameLoad)

    // internal state
    this._visible = false
    this._showTimer = null
    this._showDelay = 100 // ms to avoid flicker on very fast requests
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("turbo:load", this.onTurboLoad)
    document.removeEventListener("spinner:show", this.onSpinnerShow)
    document.removeEventListener("spinner:hide", this.onSpinnerHide)
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    document.removeEventListener("turbo:before-render", this.onBeforeStreamRender)
    document.removeEventListener("turbo:frame-render", this.onTurboFrameLoad)

    document.removeEventListener("turbo:submit-start", this.onTurboSubmitStart)
    document.removeEventListener("turbo:submit-end", this.onTurboSubmitEnd)
    document.removeEventListener("turbo:before-fetch-request", this.onTurboBeforeFetchRequest)
    document.removeEventListener("turbo:before-fetch-response", this.onTurboBeforeFetchResponse)

    document.removeEventListener("ajax:send", this.onAjaxSend)
    document.removeEventListener("ajax:beforeSend", this.onAjaxSend)
    document.removeEventListener("ajax:success", this.onAjaxComplete)
    document.removeEventListener("ajax:error", this.onAjaxComplete)
    document.removeEventListener("ajax:complete", this.onAjaxComplete)

    // jQuery cleanup
    if (window.jQuery) {
      jQuery(document).off('.spinner')
      this._jqAjaxSendHandler = null
      this._jqAjaxCompleteHandler = null
    }

    document.removeEventListener("turbo:frame-load", this.onTurboFrameLoad)

    this._clearShowTimer()
  }

  _clearShowTimer() {
    if (this._showTimer) {
      clearTimeout(this._showTimer)
      this._showTimer = null
    }
  }

  _doShow() {
    if (this.hasContainerTarget && !this._visible) {
      this.containerTarget.classList.remove("d-none")
      this._visible = true
    }
  }

  _doHide() {
    this._clearShowTimer()
    if (this.hasContainerTarget && this._visible) {
      this.containerTarget.classList.add("d-none")
      this._visible = false
    }
  }

  show() {
    // debounce to avoid flashing for very quick operations
    this._clearShowTimer()
    if (this._visible) return
    this._showTimer = setTimeout(() => this._doShow(), this._showDelay)
  }

  hide() {
    // hide immediately
    this._doHide()
  }
}
